
import { defineComponent, nextTick, onMounted, ref, watch } from 'vue';
import useWallet from '@/composables/wallet';
import useCluster from '@/composables/cluster';
import { initGemFarm } from '@/common/gem-farm';
import { PublicKey, Transaction } from '@solana/web3.js';
import ConfigPane from '@/components/ConfigPane.vue';
import FarmerDisplayRarity from '@/components/gem-farm/FarmerDisplayRarity.vue';
import Vault from '@/components/gem-bank/Vault.vue';
import { INFT } from '@/common/web3/NFTget';
import { findFarmerPDA, stringifyPKsAndBNs } from '@gemworks/gem-farm-ts';
import { initGemBank } from '@/common/gem-bank';
import { BN } from '@project-serum/anchor';

export default defineComponent({
  components: { Vault, FarmerDisplayRarity, ConfigPane },
  setup() {
    const { wallet, getWallet } = useWallet();
    const { cluster, getConnection } = useCluster();

    let gf: any;
    let gb: any;
    
    watch([wallet, cluster], async () => {
      await freshStart();
    });

    //needed in case we switch in from another window
    onMounted(async () => {
      await freshStart();
    });

    // --------------------------------------- farmer details
    const farm = ref<string>();
    const farmAcc = ref<any>();

    let farm1 = "";

    const farmerIdentity = ref<string>();
    const farmerAcc = ref<any>();
    const farmerState = ref<string>();

    const availableA = ref<string>();
    const availableB = ref<string>();

    const isLoading = ref<boolean>(false);

    const overlayMessage = ref<string>("LOADING...");

    const gemCount = ref<number>();

    const componentKey = ref<number>(0);

    //auto loading for when farm changes
    watch(farm, async () => {
      await freshStart();
    });

    const updateAvailableRewards = async () => {
      availableA.value = farmerAcc.value.rewardA.accruedReward
        .sub(farmerAcc.value.rewardA.paidOutReward)
        .toString();
      availableB.value = farmerAcc.value.rewardB.accruedReward
        .sub(farmerAcc.value.rewardB.paidOutReward)
        .toString();
    };

    const fetchFarn = async () => {
      farm1 = "AaM6gcGnD6WyybGn56kCcaJ66x26CJvZoikoqLwj9B6k";
      farmAcc.value = await gf.fetchFarmAcc(new PublicKey(farm1));
      console.log(
        `farm found at ${farm1}:`,
        stringifyPKsAndBNs(farmAcc.value)
      );
      fetchGemCount();
    };

    const fetchFarmer = async () => {
      const [farmerPDA] = await findFarmerPDA(
        new PublicKey(farm1),
        getWallet()!.publicKey!
      );
      farmerIdentity.value = getWallet()!.publicKey?.toBase58();
      farmerAcc.value = await gf.fetchFarmerAcc(farmerPDA);
      console.log(farmerAcc.value);
      farmerState.value = gf.parseFarmerState(farmerAcc.value);
      await updateAvailableRewards();
      console.log(
        `farmer found at ${farmerIdentity.value}:`,
        stringifyPKsAndBNs(farmerAcc.value)
      );
    };

    const freshStart = async () => {
      if (getWallet() && getConnection()) {
        gf = await initGemFarm(getConnection(), getWallet()!);
        gb = await initGemBank(getConnection(), getWallet()!);
        farmerIdentity.value = getWallet()!.publicKey?.toBase58();

        //reset stuff
        farmAcc.value = undefined;
        farmerAcc.value = undefined;
        farmerState.value = undefined;
        availableA.value = undefined;
        availableB.value = undefined;

        try {
          await fetchFarn();
          await fetchFarmer();
        } catch (e) {
          console.log(`farm with PK ${farm1} not found :(`);
        }
      }
    };

    const initFarmer = async () => {
      isLoading.value = true;
      overlayMessage.value = "Creating Account..."
      try {
        await gf.initFarmerWallet(new PublicKey(farm1));
        await fetchFarmer();
        isLoading.value = false;
     } catch(e) {
        overlayMessage.value = "Transaction couldn't be confirmed. Please Refresh!"
        if(e.code = 4001) isLoading.value = false;
        console.log(e);
      }    
    };

    // --------------------------------------- staking
    const beginStaking = async () => {
      isLoading.value = true;
      overlayMessage.value = "Staking..."
      try {
      await gf.stakeWallet(new PublicKey(farm1));
      isLoading.value = false;
      } catch (e) {
      overlayMessage.value = "Transaction couldn't be confirmed. Please Refresh!"
      if(e.code = 4001) isLoading.value = false;
      console.log(e);
    }
      await fetchFarmer();
      await forceRender();
      selectedNFTs.value = [];
    };

    const endStaking = async () => {
      isLoading.value = true;
      if(farmerState.value == "pendingCooldown") {
        overlayMessage.value = "Ending Cooldown..."
      } else{
        overlayMessage.value = "Unstaking..."
      }
      try {
        await gf.unstakeWallet(new PublicKey(farm1));
        isLoading.value = false;
      } catch(e) {
        overlayMessage.value = "Transaction couldn't be confirmed. Please Refresh!"
        if(e.code = 4001) isLoading.value = false;
        console.log(e);
      }      
      await fetchFarmer();
      await forceRender();
      selectedNFTs.value = [];
    };

    const claim = async () => {
      isLoading.value = true;
      overlayMessage.value = "Claiming..."
      await gf.claimWallet(
        new PublicKey(farm1),
        new PublicKey(farmAcc.value.rewardA.rewardMint!),
        new PublicKey(farmAcc.value.rewardB.rewardMint!)
      );
      await fetchFarmer();
      isLoading.value = false;
    };

    const handleRefreshFarmer = async () => {
      await fetchFarmer();
    };

    // --------------------------------------- adding extra gem
    const selectedNFTs = ref<INFT[]>([]);

    const handleNewSelectedNFT = (newSelectedNFTs: INFT[]) => {
      console.log(`selected ${newSelectedNFTs.length} NFTs`);
      selectedNFTs.value = newSelectedNFTs;
    };

    const addSingleGem = async (
      gemMint: PublicKey,
      gemSource: PublicKey,
      creator: PublicKey
    ) => {
      await gf.flashDepositWallet(
        new PublicKey(farm1),
        '1',
        gemMint,
        gemSource,
        creator
      );
      await fetchFarmer();
    };

const handleDeposit = async () => {
    isLoading.value = true;
    if (!gf || !gb)
      throw new Error("No Gem Bank client has been initialized.")

    const tx = new Transaction()

    let cont = 0;
    console.log("selected nfts:  " + selectedNFTs.value);
    for (const nft of selectedNFTs.value) {
      const creator = new PublicKey(
        nft.onchainMetadata.data.creators?.[0].address || ""
      )
      cont++;
      console.log("init tx: " + cont);
      console.log("vault:" + farmerAcc.value.vault);
      console.log(nft);
      console.log(nft.onchainMetadata.mint);
      console.log(nft.onchainMetadata.tokenAccount);
      tx.add(
        await gb.depositGemWalletIx(
          new PublicKey("wWgQ9n9UmvdaBNaAEGN1TTdDEyhHNHvYBn73p6aMEKS"),
          new PublicKey(farmerAcc.value.vault),
          new BN(1),
          new PublicKey(nft.onchainMetadata.mint),
          new PublicKey(nft.pubkey!),
          creator
        )
      )
    }

    //tx.add(await gf.stakeWalletIx(new PublicKey(farm1)))

    try {
      overlayMessage.value = "Moving NFTs to Vault..."
      const txSig = await gf.provider.send(tx)
      await getConnection().confirmTransaction(txSig)
      isLoading.value = false;
      //TODO feedback message
    } catch (e) {
      overlayMessage.value = "Transaction couldn't be confirmed. Please Refresh!"
      if(e.code = 4001) isLoading.value = false;
      console.log(e);
    }
   
    await fetchFarmer();
    
    selectedNFTs.value = [];
  }

  const handleWithdraw = async (tx: any) => {
    try{
      isLoading.value = true;
      overlayMessage.value = "Moving NFTs to Wallet..."
      const txSig = await gf.provider.send(tx);
      await getConnection().confirmTransaction(txSig)
      isLoading.value = false;
    } catch(e) {
      overlayMessage.value = "Transaction couldn't be confirmed. Please Refresh!"
      if(e.code = 4001) isLoading.value = false;
    }
    await fetchFarmer();
    selectedNFTs.value = [];
  }

  const addGems = async () => {
    await Promise.all(
      selectedNFTs.value.map((nft) => {
        const creator = new PublicKey(
          //todo currently simply taking the 1st creator
          (nft.onchainMetadata as any).data.creators[0].address
        );
        console.log('creator is', creator.toBase58());

        addSingleGem(nft.mint, nft.pubkey!, creator);
      })
    );
    console.log(
      `added another ${selectedNFTs.value.length} gems into staking vault`
    );
  };

  const fetchGemCount = async() => {
    var vaults = await gb.fetchAllVaultPDAs(new PublicKey("wWgQ9n9UmvdaBNaAEGN1TTdDEyhHNHvYBn73p6aMEKS"));
    var cleanVaults = [];
    gemCount.value = 0;
    for(var vault of vaults) {
        vault.account.gemCount = stringifyPKsAndBNs( vault.account.gemCount);
        gemCount.value += +vault.account.gemCount;
    }
  }

  const forceRender = async() => {
    componentKey.value += 1;
  }

  return {
    wallet,
    farm,
    farmAcc,
    farmer: farmerIdentity,
    farmerAcc,
    farmerState,
    availableA,
    availableB,
    initFarmer,
    beginStaking,
    endStaking,
    claim,
    handleRefreshFarmer,
    selectedNFTs,
    handleNewSelectedNFT,
    addGems,
    handleDeposit,
    handleWithdraw,
    isLoading,
    overlayMessage,
    gemCount,
    componentKey
    };
  },
});
